import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'
import { formatDate, formatTime } from '../utils/helpers'
import {
  UserGroupIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  ChartBarIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// Full class names, so Tailwind can find them in the source
const STAT_CARD_COLORS = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  primary: { bg: 'bg-primary-100', text: 'text-primary-600' },
  success: { bg: 'bg-success-100', text: 'text-success-600' }
}

const StatCard = ({ title, value, icon: Icon, color = 'primary', onClick }) => (
  <div
    className={`card ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className="card-body">
      <div className="flex items-center">
        <div className="shrink-0">
          <div className={`w-8 h-8 ${STAT_CARD_COLORS[color].bg} rounded-lg flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${STAT_CARD_COLORS[color].text}`} />
          </div>
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  </div>
)

const Dashboard = () => {
  const { isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [recentMeetups, setRecentMeetups] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAdmin) return

    const fetchDashboardData = async () => {
      try {
        const [statsResponse, meetupsResponse] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/meetups?limit=5')
        ])

        setStats(statsResponse.data)
        setRecentMeetups(meetupsResponse.data.meetups)
      } catch (error) {
        console.error('Dashboard data fout:', error)
        toast.error('Kon dashboard gegevens niet laden')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [isAdmin])

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAdmin) {
    return <Navigate to="/login" replace />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Dashboard laden...</p>
          </div>
        </div>
      </div>
    )
  }


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Overzicht van je meetups en leden
          </p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Snelle Acties
              </h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Nieuwe Meetup (Quick Actions) clicked!')
                    navigate('/admin/meetups')
                  }}
                  className="btn btn-outline w-full"
                >
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Meetups beheren
                </button>
                <button
                  type="button"
                  onClick={() => {
                    console.log('Gebruikers Beheren clicked!')
                    navigate('/admin/users')
                  }}
                  className="btn btn-outline w-full"
                >
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Leden Beheren
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Komende meetups"
              value={stats.meetups.upcomingMeetups}
              icon={CalendarDaysIcon}
              color="blue"
              onClick={() => navigate('/admin/meetups')}
            />
            <StatCard
              title="Verzonden Emails"
              value={stats.emails.totalEmails}
              icon={EnvelopeIcon}
              color="purple"
            />
            <StatCard
              title="Totaal Leden"
              value={stats.users.totalUsers}
              icon={UserGroupIcon}
              color="primary"
              onClick={() => navigate('/admin/users')}
            />
            <StatCard
              title="Geverifieerde Leden"
              value={stats.users.verifiedUsers}
              icon={UserGroupIcon}
              color="success"
              onClick={() => navigate('/admin/users')}
            />
          </div>
        )}

        {/* Meetup Statistics */}
        <div className="mb-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Meetup Statistieken
              </h2>
            </div>
            <div className="card-body">
              {recentMeetups.length > 0 ? (
                <>
                  {/* Desktop/Tablet Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Meetup
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Uitgenodigd
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Ja
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Nee
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Gewijzigd
                          </th>
                          <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Reactie %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {recentMeetups.map((meetup) => {
                          const totalInvited = meetup.stats?.totalInvited || 0
                          const yesResponses = meetup.stats?.yesResponses || 0
                          const noResponses = meetup.stats?.noResponses || 0
                          const changed = meetup.stats?.changed || 0
                          const responseRate = totalInvited > 0
                            ? Math.round(((yesResponses + noResponses) / totalInvited) * 100)
                            : 0

                          return (
                            <tr
                              key={meetup.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                              onClick={() => navigate('/admin/meetups')}
                            >
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {meetup.title}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(meetup.date)} om {formatTime(meetup.start_time)}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {totalInvited}
                              </td>
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {yesResponses}
                              </td>
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                                {noResponses}
                              </td>
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                                {changed}
                              </td>
                              <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white mr-2">
                                    {responseRate}%
                                  </span>
                                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                      className="bg-primary-600 h-2 rounded-full"
                                      style={{ width: `${responseRate}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {recentMeetups.map((meetup) => {
                      const totalInvited = meetup.stats?.totalInvited || 0
                      const yesResponses = meetup.stats?.yesResponses || 0
                      const noResponses = meetup.stats?.noResponses || 0
                      const changed = meetup.stats?.changed || 0
                      const responseRate = totalInvited > 0
                        ? Math.round(((yesResponses + noResponses) / totalInvited) * 100)
                        : 0

                      return (
                        <div
                          key={meetup.id}
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={() => navigate('/admin/meetups')}
                        >
                          <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              {meetup.title}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {formatDate(meetup.date)} om {formatTime(meetup.start_time)}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uitgenodigd</p>
                              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{totalInvited}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reactie %</p>
                              <div className="flex items-center mt-1">
                                <span className="text-lg font-semibold text-gray-900 dark:text-white mr-2">
                                  {responseRate}%
                                </span>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ja</p>
                              <p className="text-lg font-semibold text-green-600 dark:text-green-400 mt-1">{yesResponses}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nee</p>
                              <p className="text-lg font-semibold text-red-600 dark:text-red-400 mt-1">{noResponses}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gewijzigd</p>
                              <p className="text-lg font-semibold text-orange-600 dark:text-orange-400 mt-1">{changed}</p>
                            </div>
                          </div>

                          <div className="pt-2">
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full transition-all"
                                style={{ width: `${responseRate}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  Nog geen meetups met statistieken
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recente Activiteit
              </h2>
            </div>
            <div className="card-body">
              {stats?.recentActivity?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="shrink-0">
                        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {activity.type === 'user_registered' && `${activity.description} registreerde zich`}
                          {activity.type === 'meetup_created' && `Meetup "${activity.description}" werd aangemaakt`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(activity.timestamp).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  Geen recente activiteit
                </p>
              )}
            </div>
          </div>
      </div>
    </div>
  )
}

export default Dashboard